// src/components/BlogEditor.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

export default function BlogEditor({ user, onBack, editingPost = null }) {
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'fitness',
    tags: '',
    featured_image: '',
    published: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [myPosts, setMyPosts] = useState([]);
  const [showMyPosts, setShowMyPosts] = useState(false);

  // Load post data if editing
  useEffect(() => {
    if (editingPost) {
      setFormData({
        title: editingPost.title || '',
        excerpt: editingPost.excerpt || '',
        content: editingPost.content || '',
        category: editingPost.category || 'fitness',
        tags: Array.isArray(editingPost.tags) ? editingPost.tags.join(', ') : '',
        featured_image: editingPost.featured_image || '',
        published: editingPost.published || false
      });
    }
  }, [editingPost]);

  // Fetch my posts
  const fetchMyPosts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('author_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setMyPosts(data || []);
    } catch (err) {
      console.error('Error fetching my posts:', err);
    }
  }, [user.id]);

  useEffect(() => {
    fetchMyPosts();
  }, [fetchMyPosts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Process tags
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const postData = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content.trim(),
        category: formData.category,
        tags: tagsArray,
        featured_image: formData.featured_image.trim() || null,
        published: formData.published,
        author_id: user.id,
        author_name: user.email.split('@')[0], // Fallback name
        updated_at: new Date().toISOString()
      };

      if (editingPost) {
        // Update existing post
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPost.id);

        if (error) throw error;
        setSuccess('Post updated successfully!');
      } else {
        // Create new post
        postData.published_at = formData.published ? new Date().toISOString() : null;
        
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);

        if (error) throw error;
        setSuccess('Post created successfully!');
        
        // Reset form
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          category: 'fitness',
          tags: '',
          featured_image: '',
          published: false
        });
      }

      // Refresh my posts
      fetchMyPosts();
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error saving post:', err);
      setError(err.message || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      
      setSuccess('Post deleted successfully');
      fetchMyPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category || 'fitness',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      featured_image: post.featured_image || '',
      published: post.published || false
    });
    setShowMyPosts(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-purple-300 hover:text-white transition-colors"
        >
          <ChevronLeftIcon className="h-5 w-5 mr-2" />
          Back to Blog
        </button>

        <button
          onClick={() => setShowMyPosts(!showMyPosts)}
          className="bg-purple-500/80 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm"
        >
          {showMyPosts ? 'Hide My Posts' : `My Posts (${myPosts.length})`}
        </button>
      </div>

      {/* My Posts List */}
      {showMyPosts && myPosts.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4">My Posts</h3>
          <div className="space-y-3">
            {myPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white/10 border border-white/10 rounded-lg p-4 flex justify-between items-start"
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">{post.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-purple-300">
                    <span className={`px-2 py-1 rounded ${post.published ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                    <span>{post.category}</span>
                    <span>{formatDate(post.updated_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(post)}
                    className="text-blue-300 hover:text-blue-200 text-sm px-3 py-1 rounded bg-blue-500/20"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="text-red-300 hover:text-red-200 text-sm px-3 py-1 rounded bg-red-500/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor Form */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-6">
          {editingPost ? 'Edit Post' : 'Create New Post'}
        </h2>

        {error && (
          <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-500/20 border border-green-500/50 rounded-lg p-3">
            <p className="text-green-200 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              Title *
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter post title..."
              required
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              Excerpt (optional)
            </label>
            <textarea
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 h-20"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Brief summary of your post..."
            />
          </div>

          {/* Category and Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Category *
              </label>
              <select
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="fitness">Fitness</option>
                <option value="nutrition">Nutrition</option>
                <option value="mindset">Mindset</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="workout, strength, beginners"
              />
            </div>
          </div>

          {/* Featured Image URL */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              Featured Image URL (optional)
            </label>
            <input
              type="url"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={formData.featured_image}
              onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
            {formData.featured_image && (
              <div className="mt-2">
                <img
                  src={formData.featured_image}
                  alt="Preview"
                  className="h-32 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              Content *
            </label>
            <textarea
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your blog post content here...

You can use plain text or basic markdown formatting:

**Bold text**
*Italic text*

- Bullet point
- Another point

1. Numbered list
2. Another item"
              rows={20}
              required
            />
            <p className="text-purple-400 text-xs mt-2">
              Word count: {formData.content.split(/\s+/).filter(w => w.length > 0).length}
            </p>
          </div>

          {/* Published checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="published"
              className="h-5 w-5 rounded border-white/20 bg-white/10 text-purple-600 focus:ring-2 focus:ring-purple-500"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
            />
            <label htmlFor="published" className="ml-3 text-purple-200">
              Publish immediately
              <span className="block text-xs text-purple-400 mt-1">
                If unchecked, post will be saved as draft
              </span>
            </label>
          </div>

          {/* Submit buttons */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Saving...
                </div>
              ) : editingPost ? (
                'Update Post'
              ) : (
                'Create Post'
              )}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="bg-gray-500/80 hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Writing Tips */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Writing Tips</h3>
        <ul className="space-y-2 text-purple-200 text-sm">
          <li>• Keep paragraphs short and easy to read</li>
          <li>• Use clear, actionable language</li>
          <li>• Include personal experiences or examples</li>
          <li>• Break up text with headings and bullet points</li>
          <li>• Proofread before publishing</li>
        </ul>
      </div>
    </div>
  );
}
